import { ref, onUnmounted, type Ref } from 'vue'

export interface TypewriterOptions {
  /** Milliseconds per character (default: 30) */
  typingSpeed?: number
  /** Milliseconds to pause after completing a message (default: 1500) */
  pauseDuration?: number
  /** Whether to start automatically (default: false) */
  autoStart?: boolean
}

/**
 * Composable for typewriter text effect that cycles through messages.
 *
 * @param messages - Reactive array of messages to cycle through
 * @param options - Configuration options
 */
export function useTypewriter(
  messages: Ref<string[]>,
  options: TypewriterOptions = {}
) {
  const {
    typingSpeed = 30,
    pauseDuration = 1500,
    autoStart = false
  } = options

  const currentText = ref('')
  const isTyping = ref(false)
  const isActive = ref(false)

  let currentMessageIndex = 0
  let currentCharIndex = 0
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  /**
   * Type the next character of the current message.
   */
  function typeNextChar() {
    if (!isActive.value) return

    const currentMessage = messages.value[currentMessageIndex]
    if (!currentMessage) {
      // No messages, wait and retry
      timeoutId = setTimeout(typeNextChar, 100)
      return
    }

    if (currentCharIndex < currentMessage.length) {
      // Still typing current message
      currentText.value = currentMessage.slice(0, currentCharIndex + 1)
      currentCharIndex++
      isTyping.value = true
      timeoutId = setTimeout(typeNextChar, typingSpeed)
    } else {
      // Finished typing this message, pause then move to next
      isTyping.value = false
      timeoutId = setTimeout(startNextMessage, pauseDuration)
    }
  }

  /**
   * Start typing the next message in the cycle.
   */
  function startNextMessage() {
    if (!isActive.value) return

    // Move to next message (cycle back to 0 at end)
    currentMessageIndex = (currentMessageIndex + 1) % messages.value.length
    currentCharIndex = 0
    currentText.value = ''

    // Start typing
    typeNextChar()
  }

  /**
   * Start the typewriter animation.
   */
  function start() {
    if (isActive.value) return

    isActive.value = true
    currentMessageIndex = 0
    currentCharIndex = 0
    currentText.value = ''

    typeNextChar()
  }

  /**
   * Stop the typewriter animation.
   */
  function stop() {
    isActive.value = false
    isTyping.value = false

    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  /**
   * Reset and restart the animation.
   */
  function reset() {
    stop()
    currentText.value = ''
    currentMessageIndex = 0
    currentCharIndex = 0
  }

  // Auto-start if configured
  if (autoStart) {
    start()
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stop()
  })

  return {
    /** The current displayed text */
    currentText,
    /** Whether currently typing (vs paused) */
    isTyping,
    /** Whether the animation is active */
    isActive,
    /** Start the animation */
    start,
    /** Stop the animation */
    stop,
    /** Reset and stop */
    reset
  }
}
